<template>
    <div class="lsva-dashboard-view">
        <div class="lsva-dashboard-view__header">
            <h1>{{ $t('title') }}</h1>
        </div>

        <div class="lsva-dashboard-view__content">
            <div class="lsva-dashboard-view__summary">
                <div class="summary-cards">
                    <div
                        v-for="card in summaryCards"
                        :key="card.label"
                        class="summary-card"
                    >
                        <p class="summary-card__label">{{ card.label }}</p>

                        <p
                            class="summary-card__value"
                            :class="{
                                'summary-card__value--positive':
                                    card.isPositive,
                            }"
                        >
                            <template v-if="isTotalsLoading">–</template>
                            <template v-else>{{ card.value }}</template>
                        </p>

                        <p class="summary-card__hint">{{ card.hint }}</p>
                    </div>
                </div>

                <div class="chart-card">
                    <VSpinner
                        v-if="isTotalsLoading"
                        size="medium"
                        :line-fg-color="spinnerColor"
                        :speed="1"
                    />

                    <ApexChart
                        v-else
                        height="320"
                        :options="chartOptions"
                        :series="chartSeries"
                    />
                </div>
            </div>

            <LsvaDashboardVehiclesTable
                :is-loading="isVehiclesLoading"
                :vehicles="vehicles"
                @export="handleExport"
            >
                <template #filter>
                    <div class="lsva-dashboard-view__date-filter">
                        <DateInput
                            v-model="dateFilter"
                            :placeholder="$t('periodPlaceholder')"
                            :max-range="maxRangeDays"
                            range
                        />
                    </div>
                </template>
            </LsvaDashboardVehiclesTable>
        </div>
    </div>
</template>

<script>
import ApexChart from 'vue-apexcharts'
import VSpinner from 'vue-simple-spinner'
import moment from 'moment'
import { encode } from 'windows-1252/windows-1252.mjs'

import { domHelper, formatHelper, httpHelper } from '@/utils'
import DateInput from '@/components/redesigned/DateInput'
import LsvaDashboardVehiclesTable from '@/components/lsva/LsvaDashboardVehiclesTable'

const colorBilled = '#3d5979'
const colorSaved = '#41b883'
const defaultRange = 12
const maxRange = 36

export default {
    name: 'LsvaDashboardView',
    components: {
        ApexChart,
        DateInput,
        LsvaDashboardVehiclesTable,
        VSpinner,
    },
    data() {
        return {
            dateFilter: [
                moment()
                    .utc()
                    .subtract(defaultRange - 1, 'months')
                    .startOf('month')
                    .toDate(),
                moment()
                    .utc()
                    .endOf('day')
                    .toDate(),
            ],
            isTotalsLoading: false,
            isVehiclesLoading: false,
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
            totals: [],
            vehicles: [],
        }
    },
    computed: {
        amountBilled() {
            return this.totals.reduce((acc, cur) => acc + cur.amount_total, 0)
        },
        amountSaved() {
            return this.totals.reduce((acc, cur) => acc + cur.amount_saved, 0)
        },
        savedShare() {
            const reference = this.amountBilled + this.amountSaved
            return reference > 0 ? (this.amountSaved / reference) * 100 : 0
        },
        summaryCards() {
            const hint = this.$tc('lastMonths', defaultRange)
            return [
                {
                    label: this.$t('billed'),
                    value: `CHF ${this.amountBilled.toLocaleString('de-CH', {
                        maximumFractionDigits: 0,
                    })}`,
                    hint,
                },
                {
                    label: this.$t('saved'),
                    value: `CHF ${this.amountSaved.toLocaleString('de-CH', {
                        maximumFractionDigits: 0,
                    })}`,
                    hint,
                    isPositive: true,
                },
                {
                    label: this.$t('savedShare'),
                    value: `${this.savedShare.toLocaleString('de-CH', {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                    })}%`,
                    hint,
                },
            ]
        },
        chartSeries() {
            return [
                {
                    name: this.$t('billed'),
                    data: this.totals.map(item => item.amount_total),
                },
                {
                    name: this.$t('saved'),
                    data: this.totals.map(item => item.amount_saved),
                },
            ]
        },
        chartOptions() {
            return {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false,
                    },
                },
                colors: [colorBilled, colorSaved],
                dataLabels: {
                    enabled: false,
                },
                tooltip: {
                    y: {
                        formatter: value => `CHF ${value}`,
                    },
                },
                xaxis: {
                    categories: this.totals.map(item =>
                        moment(item.period, 'YYYY-MM-DD').format('MMM YY')
                    ),
                },
                yaxis: [
                    {
                        labels: {
                            formatter: formatHelper.kFormatter,
                        },
                    },
                ],
            }
        },
        maxRangeDays() {
            return (maxRange - 1) * 30
        },
    },
    watch: {
        dateFilter() {
            this.fetchVehicles()
        },
    },
    mounted() {
        this.fetchTotals()
        this.fetchVehicles()
    },
    methods: {
        async fetchTotals() {
            try {
                this.isTotalsLoading = true
                const { data } = await httpHelper.get(
                    '/chartera/lsva-bills/monthly/'
                )
                this.totals = data
            } catch {
                this.totals = []
            } finally {
                this.isTotalsLoading = false
            }
        },
        async fetchVehicles() {
            try {
                this.isVehiclesLoading = true
                const { data } = await httpHelper.get(
                    '/chartera/lsva-bills/vehicles/',
                    {
                        params: {
                            start: moment(this.dateFilter[0]).format(
                                'YYYY-MM-DD'
                            ),
                            end: moment(this.dateFilter[1]).format(
                                'YYYY-MM-DD'
                            ),
                        },
                    }
                )
                this.vehicles = data
            } catch {
                this.vehicles = []
            } finally {
                this.isVehiclesLoading = false
            }
        },
        handleExport() {
            const rows = [
                [
                    this.$t('columns.vehicle'),
                    this.$t('columns.vrn'),
                    this.$t('columns.transportGroup'),
                    this.$t('columns.distanceDeclared'),
                    this.$t('columns.distanceAssessed'),
                    this.$t('columns.tonKilometers'),
                    this.$t('columns.amount'),
                    this.$t('columns.amountSaved'),
                ],
                ...this.vehicles.map(item => [
                    item.name,
                    item.vrn,
                    item.transport_group,
                    item.distance_declared,
                    item.distance_assessed,
                    item.ton_kilometers,
                    item.amount_total,
                    item.amount_saved,
                ]),
            ]

            const encoded = encode(
                `sep=;\r\n${rows.map(row => row.join(';')).join('\r\n')}`
            )
            const blob = new Blob([encoded], {
                type: 'text/csv;charset=windows-1252',
            })

            const filename = `lsva-${moment(this.dateFilter[0]).format(
                'YYYY-MM-DD'
            )}-${moment(this.dateFilter[1]).format('YYYY-MM-DD')}.csv`

            domHelper.downloadBlob(blob, filename)
        },
    },
}
</script>

<i18n>
{
    "en": {
        "billed": "Billed",
        "columns": {
            "amount": "LSVA CHF",
            "amountSaved": "Saved CHF",
            "distanceAssessed": "Assessed km",
            "distanceDeclared": "Declared km",
            "tonKilometers": "tkm",
            "transportGroup": "Transport group",
            "vehicle": "Vehicle",
            "vrn": "License plate"
        },
        "lastMonths": "last {n} month | last {n} months",
        "periodPlaceholder": "Period",
        "saved": "Saved with E",
        "savedShare": "Share saved",
        "title": "LSVA Dashboard"
    },
    "de": {
        "billed": "In Rechnung gestellt",
        "columns": {
            "amount": "LSVA CHF",
            "amountSaved": "Eingespart CHF",
            "distanceAssessed": "Veranlagte km",
            "distanceDeclared": "Deklarierte km",
            "tonKilometers": "tkm",
            "transportGroup": "Bereich",
            "vehicle": "Fahrzeug",
            "vrn": "Kennzeichen"
        },
        "lastMonths": "letzter {n} Monat | letzte {n} Monate",
        "periodPlaceholder": "Periode",
        "saved": "Eingespart mit E",
        "savedShare": "Anteil gespart",
        "title": "LSVA Dashboard"
    },
    "fr": {
        "billed": "Facturé",
        "columns": {
            "amount": "RPLP CHF",
            "amountSaved": "Économisé CHF",
            "distanceAssessed": "km taxés",
            "distanceDeclared": "km déclarés",
            "tonKilometers": "tkm",
            "transportGroup": "Secteur",
            "vehicle": "Véhicule",
            "vrn": "Plaque d'immatriculation"
        },
        "lastMonths": "{n} dernier mois | {n} derniers mois",
        "periodPlaceholder": "Période",
        "saved": "Économisé avec E",
        "savedShare": "Part économisée",
        "title": "Tableau de bord RPLP"
    },
    "it": {
        "billed": "Fatturato",
        "columns": {
            "amount": "TTPCP CHF",
            "amountSaved": "Risparmiato CHF",
            "distanceAssessed": "km tassati",
            "distanceDeclared": "km dichiarati",
            "tonKilometers": "tkm",
            "transportGroup": "Settore",
            "vehicle": "Veicolo",
            "vrn": "Targa"
        },
        "lastMonths": "ultimo {n} mese | ultimi {n} mesi",
        "periodPlaceholder": "Periodo",
        "saved": "Risparmiato con E",
        "savedShare": "Quota risparmiata",
        "title": "Dashboard TTPCP"
    }
}
</i18n>

<style lang="scss" scoped>
.lsva-dashboard-view {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;

    &__header {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 1.5rem 2rem;
        background-color: $color-gray-lighter-new;
        border-bottom: $style-border;

        h1 {
            font-size: 28px;
            color: $color-primary;
        }
    }

    &__date-filter {
        width: 200px;
    }

    &__content {
        width: 100%;
        overflow: auto;
    }

    &__summary {
        padding: 1.5rem 2rem;
        max-width: 72rem;
        margin: 0 auto;
    }
}

.summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.summary-card {
    padding: 1.5rem;
    border: $style-border;
    border-radius: 0.75rem;

    &__label {
        font-size: 0.875rem;
        color: $color-gray-darker;
    }

    &__value {
        margin: 0.25rem 0;
        font-size: 1.875rem;
        font-weight: 600;

        &--positive {
            color: $color-green;
        }
    }

    &__hint {
        font-size: 0.75rem;
        color: $color-gray-darker;
    }
}

.chart-card {
    padding: 1rem;
    border: $style-border;
    border-radius: 0.75rem;
    overflow-x: auto;
}

@include respond-to('for-mobile-down') {
    .lsva-dashboard-view {
        &__summary {
            padding: 1rem;
        }
    }

    .summary-cards {
        grid-template-columns: 1fr;
    }
}
</style>
